// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { updateEMRHub } from "../../api";

type UpdateEMRHubPayload = {
  hubId: string;
  alias: string;
};

export const useUpdateEMRHub = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async ({ hubId, ...payload }: UpdateEMRHubPayload) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateEMRHub(token, clinicId, hubId, payload);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (_, { hubId }) => {
      queryClient.invalidateQueries({ queryKey: ["emr-hubs", clinicId] });
      queryClient.invalidateQueries({
        queryKey: ["emr-hubs", clinicId, hubId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("EMR hub updated successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("EMR hub could not be updated!"),
          t
        ),
      });
    },
  });
};
